/**
 * FASE 25.3 — MUTATION DEDUPLICATION
 *
 * Content-based dedup to prevent double-submits from rapid taps or
 * network retries. Uses a deterministic hash of (toolId + sorted payload).
 *
 * INVARIANTS:
 *  - Dedup window: 5 seconds (short enough to allow intentional repeats)
 *  - No Supabase interaction — pure in-memory
 *  - Stale entries self-clear on next check or explicit cleanup
 */

const DEDUP_WINDOW_MS   = 5_000;
const STALE_CUTOFF_MS   = 30 * 60 * 1000; // 30 minutes

/** @type {Map<string, number>} hash → timestamp */
const _seen = new Map();

/* ─── Hash ────────────────────────────────────────────────────── */

function hashMutation(toolId, payload) {
  const stable = toolId + JSON.stringify(payload, Object.keys(payload ?? {}).sort());
  let h = 5381;
  for (let i = 0; i < stable.length; i++) {
    h = ((h << 5) + h) ^ stable.charCodeAt(i);
    h = h >>> 0;
  }
  return h.toString(36);
}

/* ─── Public API ─────────────────────────────────────────────── */

/**
 * Returns true if this exact mutation was registered within DEDUP_WINDOW_MS.
 * @param {string} toolId
 * @param {object} payload — the validated (clean) payload
 */
export function isMutationDuplicate(toolId, payload) {
  pruneStale();
  const hash = hashMutation(toolId, payload);
  const last = _seen.get(hash);
  return last != null && Date.now() - last < DEDUP_WINDOW_MS;
}

/**
 * Register that a mutation is about to execute.
 * Call BEFORE the adapter to block retries during the dedup window.
 * @param {string} toolId
 * @param {object} payload — the validated (clean) payload
 */
export function registerMutation(toolId, payload) {
  const hash = hashMutation(toolId, payload);
  _seen.set(hash, Date.now());
}

/**
 * Remove entries older than STALE_CUTOFF_MS.
 */
export function pruneStale() {
  const cutoff = Date.now() - STALE_CUTOFF_MS;
  for (const [hash, ts] of _seen) {
    if (ts < cutoff) _seen.delete(hash);
  }
}

/** Returns dedup stats for the debug surface. */
export function getMutationDedupStats() {
  pruneStale();
  return { activeEntries: _seen.size };
}
